/**
 * Полноэкранное оверлейное меню.
 *
 * Разметку рендерит snippets/menu-overlay.liquid. Кнопка открытия живёт в шапке и связана
 * с оверлеем через aria-controls="<id оверлея>"; её aria-expanded синхронизирует этот класс.
 */

const OPEN_BODY_CLASS = 'menu-overlay-open';
const ESCAPE_KEY = 'Escape';
const TAB_KEY = 'Tab';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const FOCUS_RING_SUPPRESSED_CLASS = 'focus-ring-suppressed';

/*
 * iOS Safari применяет :focus-visible и к программному фокусу после тапа, поэтому
 * перенос фокуса на крестик/бургер рисовал вокруг них кольцо. Следим за модальностью
 * ввода: кольцо оставляем только когда фокус двигает клавиатура.
 */
let lastInputWasKeyboard = false;
window.addEventListener('keydown', () => {
  lastInputWasKeyboard = true;
});
window.addEventListener('pointerdown', () => {
  lastInputWasKeyboard = false;
});

/** Переносит фокус, пряча кольцо, если фокус двигает тап/клик, а не клавиатура. */
function moveFocusRespectingModality(element) {
  element.focus();
  if (lastInputWasKeyboard) return;

  element.classList.add(FOCUS_RING_SUPPRESSED_CLASS);
  element.addEventListener(
    'blur',
    () => element.classList.remove(FOCUS_RING_SUPPRESSED_CLASS),
    { once: true }
  );
}

/** Экранирование id для использования в селекторе: CSS.escape есть не везде. */
function escapeSelectorValue(value) {
  if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
  return value.replace(/["\\]/g, '\\$&');
}

class MenuOverlay extends HTMLElement {
  constructor() {
    super();
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onOverlayClick = this.onOverlayClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.isInitialized = false;
    this.openedBy = null;
    this.inertedElements = [];
  }

  connectedCallback() {
    if (this.parentElement !== document.body) {
      // sticky-header темы вешает transform на обёртку шапки, а transform создаёт содержащий
      // блок для position: fixed и ломает полноэкранный оверлей. Переносим его в body.
      // После переноса connectedCallback вызовется повторно и уйдёт в инициализацию ниже.
      this.removeStaleCopies();
      document.body.appendChild(this);
      return;
    }

    if (!this.isInitialized) {
      this.isInitialized = true;
      this.closeButton = this.querySelector('[data-menu-overlay-close]');
      this.addEventListener('click', this.onOverlayClick);
    }

    // Слушатель на document снимается в disconnectedCallback, поэтому вешаем его при каждом
    // подключении. Повторное добавление той же функции браузер игнорирует.
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeyDown);
    this.releaseBackground();
    document.documentElement.classList.remove(OPEN_BODY_CLASS);
  }

  /** Редактор темы перерисовывает секцию шапки, а перенесённая в body копия остаётся жить. */
  removeStaleCopies() {
    if (!this.id) return;
    document
      .querySelectorAll(`menu-overlay#${escapeSelectorValue(this.id)}`)
      .forEach((element) => {
        if (element !== this) element.remove();
      });
  }

  get openers() {
    if (!this.id) return [];
    const selector = `[aria-controls="${escapeSelectorValue(this.id)}"]`;
    return Array.from(document.querySelectorAll(selector)).filter(
      (element) => !this.contains(element)
    );
  }

  onDocumentClick(event) {
    if (!this.id || !(event.target instanceof Element)) return;
    const opener = event.target.closest(`[aria-controls="${escapeSelectorValue(this.id)}"]`);
    if (!opener || this.contains(opener)) return;

    event.preventDefault();
    if (this.hidden) {
      this.open(opener);
    } else {
      this.close();
    }
  }

  onOverlayClick(event) {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('[data-menu-overlay-close]')) {
      this.close();
      return;
    }

    const toggle = event.target.closest('[data-menu-overlay-toggle]');
    if (toggle && this.contains(toggle)) {
      this.setDisclosure(toggle, toggle.getAttribute('aria-expanded') !== 'true');
    }
  }

  onKeyDown(event) {
    if (event.key === ESCAPE_KEY) {
      event.preventDefault();
      this.handleEscape(event.target);
      return;
    }

    if (event.key === TAB_KEY) this.trapFocus(event);
  }

  /** Escape сначала схлопывает раскрытый подпункт, в котором находится фокус, и только потом закрывает меню. */
  handleEscape(target) {
    const activeToggle =
      target instanceof Element
        ? target.closest('[data-menu-overlay-toggle][aria-expanded="true"]')
        : null;
    if (activeToggle && this.contains(activeToggle)) {
      this.setDisclosure(activeToggle, false);
      return;
    }

    const region = target instanceof Element ? target.closest('[data-menu-overlay-disclosure]') : null;
    if (region && this.contains(region) && region.id) {
      const owner = this.querySelector(`[aria-controls="${escapeSelectorValue(region.id)}"]`);
      if (owner) {
        this.setDisclosure(owner, false);
        owner.focus();
        return;
      }
    }

    this.close();
  }

  setDisclosure(toggle, shouldExpand) {
    toggle.setAttribute('aria-expanded', String(shouldExpand));
    const controlled = document.getElementById(toggle.getAttribute('aria-controls'));
    if (controlled) controlled.hidden = !shouldExpand;
  }

  getFocusableElements() {
    return Array.from(this.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
      if (element.closest('[hidden]')) return false;
      return element.offsetWidth > 0 || element.offsetHeight > 0 || element.getClientRects().length > 0;
    });
  }

  /**
   * Запасной вариант ловушки фокуса для браузеров без inert: там фон уже недоступен,
   * но лишний цикл Tab внутри диалога не мешает.
   */
  trapFocus(event) {
    const focusable = this.getFocusableElements();
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || !this.contains(active))) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /** Прячет от клавиатуры и скринридеров всё, что лежит за оверлеем. */
  isolateBackground() {
    if (!('inert' in HTMLElement.prototype)) return;

    this.inertedElements = Array.from(document.body.children).filter(
      (element) => element !== this && !element.inert
    );
    this.inertedElements.forEach((element) => {
      element.inert = true;
    });
  }

  releaseBackground() {
    this.inertedElements.forEach((element) => {
      element.inert = false;
    });
    this.inertedElements = [];
  }

  setOpenersExpanded(isExpanded) {
    this.openers.forEach((opener) => opener.setAttribute('aria-expanded', String(isExpanded)));
  }

  open(opener) {
    if (!this.hidden) return;

    this.openedBy = opener || document.activeElement;
    this.hidden = false;
    document.documentElement.classList.add(OPEN_BODY_CLASS);
    this.setOpenersExpanded(true);
    this.isolateBackground();
    document.addEventListener('keydown', this.onKeyDown);

    const target = this.closeButton || this.getFocusableElements()[0];
    if (target) moveFocusRespectingModality(target);
  }

  close() {
    if (this.hidden) return;

    this.hidden = true;
    document.documentElement.classList.remove(OPEN_BODY_CLASS);
    this.setOpenersExpanded(false);
    this.releaseBackground();
    document.removeEventListener('keydown', this.onKeyDown);

    if (this.openedBy && document.contains(this.openedBy)) {
      moveFocusRespectingModality(this.openedBy);
    }
    this.openedBy = null;
  }
}

if (!customElements.get('menu-overlay')) {
  customElements.define('menu-overlay', MenuOverlay);
}
